/**
 * OCR 核对页面
 * ------------------------------------------------------------
 * 功能：左侧 PDF 页面缩略图 + 右侧 OCR 文本（可直接编辑）
 * 流程：打开 → 逐页校对 → 保存修改 → 全部确认后 → 结构化题目 → 入库
 *
 * 数据层级（严格分开，绝不互相覆盖）：
 *   原始 PDF        → data/raw/exercises/xxx.pdf   （不发布，仅元信息）
 *   OCR 原文        → data/ocr/xxx/page-NN.txt      （只读，不覆盖）
 *   页面缩略图      → data/ocr/xxx/pages/page-NN.jpg  （视觉对照）
 *   人工修正版      → IndexedDB（imports 集合）       （保存用户编辑）
 *   结构化题目      → IndexedDB（questions 集合）     （确认入库后才正式可见）
 *
 * 路由：#/ocr/:ocrId   （ocrId 对应 library.json 里 item.id 或 data/ocr/ 目录名）
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';
import { makeQuestion } from '../core/models.js';
import { navigate, currentPath } from '../core/router.js';
import { escapeHtml, toast, nowISO, uid } from '../utils/index.js';
import { emit, EVENTS } from '../core/events.js';

/** 从 URL hash 里拿 ocrId */
function parseParams() {
  const hash = location.hash || '';
  const m = hash.match(/#\/ocr\/([^?\/]+)/);
  return { ocrId: m ? decodeURIComponent(m[1]) : null };
}

/** library.json 缓存 */
let libraryCache = null;
async function loadLibrary() {
  if (libraryCache) return libraryCache;
  const r = await fetch('./data/library.json');
  libraryCache = await r.json();
  return libraryCache;
}

/** 根据 ocrId 找 library item */
async function findItem(ocrId) {
  const lib = await loadLibrary();
  // 直接 id 匹配
  let item = lib.items.find(i => i.id === ocrId);
  if (item) return item;
  // ocr 目录名匹配
  item = lib.items.find(i => (i.ocr?.dir || '').endsWith('/' + ocrId) || (i.ocr?.dir === ocrId));
  return item || null;
}

/** 读取一页 OCR 原文 */
async function loadOcrText(ocrDir, pageNum) {
  // ocrDir 在 library.json 里是 "ocr/xxx"，对应 data/ocr/xxx
  const url = `./data/${ocrDir}/page-${String(pageNum).padStart(2, '0')}.txt`;
  try {
    const r = await fetch(url);
    if (!r.ok) return '';
    return await r.text();
  } catch { return ''; }
}

/** 页面缩略图 URL */
function thumbUrl(ocrDir, pageNum) {
  return `./data/${ocrDir}/pages/page-${String(pageNum).padStart(2, '0')}.jpg`;
}

/* ------------------------- 存储：人工修正版 ------------------------- */

/** 草稿 key（IndexedDB IMPORTS 集合里，每个 PDF 一条） */
function draftId(item) {
  return `ocr-${item.id}`;
}

async function loadDraft(item) {
  return getStorage().get(COLLECTIONS.IMPORTS, draftId(item));
}

async function saveDraft(item, pages, currentPage, confirmed = []) {
  const storage = getStorage();
  const existing = await storage.get(COLLECTIONS.IMPORTS, draftId(item));
  const row = {
    id: draftId(item),
    type: 'ocr_review',
    sourceItemId: item.id,
    sourceTitle: item.title,
    ocrDir: item.ocr.dir,
    totalPages: item.ocr.pages,
    status: 'reviewing',
    currentPage,
    // pages: { "1": { text, modified, confirmed } }
    pages,
    // confirmed: 已确认的页码列表（最终全部确认后才进题库）
    confirmedPages: confirmed,
    updatedAt: nowISO(),
    createdAt: existing?.createdAt || nowISO(),
  };
  await storage.put(COLLECTIONS.IMPORTS, row);
  return row;
}

async function commitToBank(item, pages) {
  /**
   * 把所有 confirmed=true 的页面送进结构化提取 → 生成 draft questions → 入库
   * 规则：
   *   - 这一步只做"尽量识别结构"，识别不出来的保留原文 + 标记待确认
   *   - 题目在 QUESTIONS 集合里 status='pending_review'，学生端不显示
   *   - 用户在题库页最终点"确认入库"才改 status='confirmed'
   */
  const questions = [];
  const confirmedNums = Object.entries(pages)
    .filter(([, p]) => p.confirmed)
    .map(([n]) => parseInt(n, 10))
    .sort((a, b) => a - b);

  if (!confirmedNums.length) {
    toast('请先确认至少一页再入库', 'warn');
    return 0;
  }

  let extracted = 0;
  for (const pageNum of confirmedNums) {
    const p = pages[String(pageNum)];
    if (!p) continue;
    const qs = extractQuestionsFromText(p.text, pageNum, item);
    for (const q of qs) {
      questions.push(q);
      extracted++;
    }
  }

  const storage = getStorage();
  for (const q of questions) {
    await storage.put(COLLECTIONS.QUESTIONS, q);
  }

  // 更新草稿状态为已提交
  const draft = await loadDraft(item);
  if (draft) {
    draft.status = 'committed';
    draft.committedAt = nowISO();
    draft.committedQuestions = extracted;
    draft.confirmedPages = confirmedNums;
    await storage.put(COLLECTIONS.IMPORTS, draft);
  }

  emit(EVENTS.CONTENT_CHANGED);
  return extracted;
}

/**
 * 从一页文本里尽量识别题目结构
 * 注意：不完整的、识别不出来的，留完整原文 + 标记待确认。绝不自行编造。
 */
function extractQuestionsFromText(text, pageNum, item) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const questions = [];

  // 匹配题号：( )1. / 1. / (1) / 一、 / 二、 / A. 选择题项
  const qNumRe = /^[（(]?\s*(\d{1,2})\s*[)）.、]/;
  const choiceRe = /^([A-D])[\.、\s]\s*(.+)/;
  const sentenceRe = /[A-Za-z]/;  // 含英文字母就认为是英文内容

  let current = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // 命中新题号 → 保存上一题 + 开启新题
    const m = line.match(qNumRe);
    if (m && current) {
      questions.push(buildQuestion(current, pageNum, item));
      current = { num: parseInt(m[1], 10), lines: [line], options: [], answerLine: '' };
    } else if (m && !current) {
      current = { num: parseInt(m[1], 10), lines: [line], options: [], answerLine: '' };
    } else if (current) {
      // 选项行
      const cm = line.match(choiceRe);
      if (cm) {
        current.options.push({ key: cm[1], text: cm[2].trim() });
      } else {
        current.lines.push(line);
      }
    }
  }
  if (current) questions.push(buildQuestion(current, pageNum, item));

  // 如果一页里完全没识别出结构化题目 → 把整页作为"原文块"存，status='pending_review'
  if (!questions.length && lines.some(sentenceRe.test.bind(sentenceRe))) {
    questions.push(makeQuestion({
      id: uid('q'),
      textbookId: '',
      unitId: '',
      type: 'text',
      skill: 'R',
      prompt: `【第 ${pageNum} 页原文】\n${text}`,
      options: [],
      answer: [],
      explain: '',
      hint: '',
      audioUrl: '',
      audio: '',
      refType: 'ocr',
      refId: `${item.id}-p${pageNum}`,
      origin: 'ocr',
      sourceFile: item.fileName,
      page: pageNum,
      status: 'confirmed',
      needsReview: true,
      confidence: 0.3,
    }));
  }

  return questions;
}

function buildQuestion(ctx, pageNum, item) {
  // 题号行里去掉题号前缀当作题干第一行
  const promptLines = ctx.lines.map(l => l.replace(/^[（(]?\s*\d{1,2}\s*[)）.、]\s*/, '').trim()).filter(Boolean);
  const prompt = promptLines.join('\n');
  const hasEnglish = /[A-Za-z]/.test(prompt);
  const hasOptions = ctx.options.length >= 2;

  let type = 'choice';
  if (hasOptions) type = 'choice';
  else if (/填空|补全|填写/.test(prompt) || prompt.includes('____')) type = 'fill';
  else if (/判断/.test(prompt) || /对.*错/.test(prompt)) type = 'judge';
  else if (/排序/.test(prompt)) type = 'order';
  else type = hasEnglish ? 'fill' : 'text';

  let answer = [];
  if (hasOptions && promptLines.length >= 2) {
    // 末行可能是答案 ( A / B / A B )
    const last = promptLines[promptLines.length - 1];
    const ansMatch = last.match(/[A-D](?:[ ,、]?[A-D])*/);
    if (ansMatch && last.length <= 12) {
      answer = ansMatch[0].split(/[,、\s]+/).filter(Boolean);
      // 从 prompt 里移除答案行
      promptLines.pop();
    }
  }

  return makeQuestion({
    type,
    skill: hasEnglish ? 'W' : 'G',
    prompt: promptLines.join('\n'),
    options: hasOptions ? ctx.options.map(o => `${o.key}. ${o.text}`) : [],
    answer,
    explain: '',
    hint: '',
    audioUrl: '',
    audio: '',
    refType: 'ocr',
    refId: `${item.id}-p${pageNum}-q${ctx.num}`,
    origin: 'ocr',
    sourceFile: item.fileName,
    page: pageNum,
    status: 'confirmed',
    needsReview: true,
    confidence: hasOptions && hasEnglish ? 0.6 : 0.4,
  });
}

/* ========================== 页面渲染 ========================== */

export default async function renderOcrReview(mount, params = {}) {
  const { ocrId } = parseParams();
  if (!ocrId) {
    mount.innerHTML = `<div class="empty"><p>缺少参数</p><a class="btn btn-primary" href="#/import">返回导入中心</a></div>`;
    return;
  }

  const item = await findItem(ocrId);
  if (!item) {
    mount.innerHTML = `<div class="empty"><p>找不到对应资料</p><a class="btn btn-primary" href="#/import">返回导入中心</a></div>`;
    return;
  }

  if (!item.ocr?.done) {
    mount.innerHTML = `<div class="empty"><p>这份资料的 OCR 还没完成</p><a class="btn btn-primary" href="#/import">返回导入中心</a></div>`;
    return;
  }

  const totalPages = item.ocr.pages;
  const ocrDir = item.ocr.dir;

  // 加载已保存的草稿（如果有）
  const draft = await loadDraft(item);
  const pages = draft?.pages || {};
  let currentPage = draft?.currentPage || 1;
  const confirmedPages = draft?.confirmedPages || [];

  // 如果是新草稿，加载第一页 OCR 原文
  if (!pages[String(currentPage)]) {
    const txt = await loadOcrText(ocrDir, currentPage);
    pages[String(currentPage)] = { text: txt, modified: false, confirmed: confirmedPages.includes(currentPage) };
  }

  mount.innerHTML = `
    <div class="ocr-head">
      <div class="row" style="justify-content:space-between;align-items:center">
        <div>
          <h1 style="margin:0">📖 OCR 核对</h1>
          <p class="muted" style="margin:4px 0 0 0;font-size:13px">
            ${escapeHtml(item.title)} · 共 ${totalPages} 页
            <span class="tag blue">OCR 原文只读</span>
            <span class="tag green">人工修正可编辑</span>
          </p>
        </div>
        <div class="row" style="gap:8px">
          <button class="btn btn-ghost" id="btn-back" type="button">← 返回</button>
          <button class="btn btn-primary" id="btn-commit" type="button" ${confirmedPages.length === 0 ? 'disabled' : ''}>
            确认 ${confirmedPages.length} 页 → 入题库
          </button>
        </div>
      </div>
      <div class="progress-bar mt-8" style="height:6px">
        <div style="background:var(--green);width:${(confirmedPages.length / totalPages * 100).toFixed(1)}%;height:100%;border-radius:3px;transition:width .3s"></div>
      </div>
      <div class="muted mt-4" style="font-size:12px">
        已确认：${confirmedPages.length} / ${totalPages} 页
      </div>
    </div>

    <div class="ocr-split">
      <!-- 左侧：页面缩略图列表 -->
      <aside class="ocr-thumbs">
        <div class="thumbs-header">页面导航</div>
        <div class="thumbs-list" id="thumbs-list"></div>
      </aside>

      <!-- 中间：PDF 视觉对照 -->
      <section class="ocr-visual">
        <div class="visual-header">PDF 页面（原始图像，只读）</div>
        <div class="visual-frame">
          <img id="page-img" alt="PDF page" loading="lazy">
          <div class="zoom-ctrl">
            <button class="btn btn-sm btn-ghost" id="zoom-out" type="button">－</button>
            <span id="zoom-pct">100%</span>
            <button class="btn btn-sm btn-ghost" id="zoom-in" type="button">＋</button>
          </div>
        </div>
      </section>

      <!-- 右侧：可编辑文本 -->
      <section class="ocr-edit">
        <div class="edit-header">
          <span>OCR 识别结果（可直接修改）</span>
          <span class="row" style="gap:6px;margin-left:auto">
            <button class="btn btn-sm btn-ghost" id="btn-revert" type="button" title="恢复本页为 OCR 原文">↺ 恢复原文</button>
            <button class="btn btn-sm btn-ghost" id="btn-save-page" type="button">💾 保存本页</button>
            <label class="checkbox">
              <input type="checkbox" id="chk-confirmed" ${pages[String(currentPage)]?.confirmed ? 'checked' : ''}>
              此页已核对确认
            </label>
          </span>
        </div>
        <textarea id="page-text" spellcheck="false" placeholder="正在加载 OCR 原文…"></textarea>
        <div class="edit-footer">
          <button class="btn btn-sm" id="btn-prev" type="button" ${currentPage <= 1 ? 'disabled' : ''}>← 上一页</button>
          <span class="page-indicator">第 <input type="number" id="page-num" min="1" max="${totalPages}" value="${currentPage}"> / ${totalPages} 页</span>
          <button class="btn btn-sm" id="btn-next" type="button" ${currentPage >= totalPages ? 'disabled' : ''}>下一页 →</button>
        </div>
      </section>
    </div>

    <style>
      .ocr-head { padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-card); }
      .ocr-split { display: grid; grid-template-columns: 140px 1fr 1fr; gap: 0; height: calc(100vh - 120px); }
      .ocr-thumbs { border-right: 1px solid var(--border); overflow-y: auto; padding: 8px; background: var(--bg-card); }
      .thumbs-header { font-size: 11px; color: var(--muted); padding: 4px; border-bottom: 1px solid var(--border); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; }
      .thumbs-list { display: flex; flex-direction: column; gap: 6px; }
      .thumb-item { cursor: pointer; border: 2px solid transparent; border-radius: 6px; overflow: hidden; background: var(--bg); position: relative; }
      .thumb-item:hover { border-color: var(--primary); }
      .thumb-item.active { border-color: var(--primary); box-shadow: 0 0 0 2px rgba(64,128,255,0.2); }
      .thumb-item.confirmed::after { content: '✓'; position: absolute; top: 4px; right: 4px; background: var(--green); color: white; width: 20px; height: 20px; border-radius: 50%; font-size: 12px; display: flex; align-items: center; justify-content: center; }
      .thumb-item.modified::before { content: '●'; position: absolute; top: 4px; left: 4px; color: var(--orange); font-size: 14px; text-shadow: 0 0 3px white; }
      .thumb-item img { width: 100%; display: block; }
      .thumb-num { font-size: 10px; text-align: center; padding: 2px; background: var(--bg); color: var(--muted); }
      .ocr-visual { display: flex; flex-direction: column; border-right: 1px solid var(--border); background: var(--bg-card); overflow: hidden; }
      .visual-header { font-size: 11px; color: var(--muted); padding: 8px 12px; border-bottom: 1px solid var(--border); text-transform: uppercase; letter-spacing: 1px; }
      .visual-frame { flex: 1; overflow: auto; padding: 16px; text-align: center; position: relative; background: var(--bg); }
      .visual-frame img { max-width: 100%; box-shadow: 0 2px 8px rgba(0,0,0,0.1); transition: transform .15s; transform-origin: top center; }
      .zoom-ctrl { position: sticky; bottom: 12px; display: inline-flex; gap: 4px; align-items: center; background: var(--bg-card); border: 1px solid var(--border); border-radius: 20px; padding: 4px 10px; margin-top: 8px; font-size: 12px; }
      .ocr-edit { display: flex; flex-direction: column; background: var(--bg-card); }
      .edit-header { display: flex; align-items: center; padding: 8px 12px; border-bottom: 1px solid var(--border); font-size: 12px; color: var(--muted); gap: 8px; }
      .edit-header .checkbox { display: flex; align-items: center; gap: 4px; font-size: 12px; color: var(--text); cursor: pointer; white-space: nowrap; }
      #page-text { flex: 1; border: none; padding: 16px; font-family: 'Menlo','Monaco','Consolas','Courier New',monospace; font-size: 14px; line-height: 1.7; resize: none; background: var(--bg); color: var(--text); outline: none; }
      .edit-footer { display: flex; align-items: center; gap: 12px; padding: 8px 12px; border-top: 1px solid var(--border); background: var(--bg-card); }
      .page-indicator { font-size: 13px; color: var(--muted); }
      .page-indicator input { width: 48px; padding: 2px 4px; text-align: center; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-card); }
      @media (max-width: 900px) {
        .ocr-split { grid-template-columns: 80px 1fr; grid-template-rows: 200px 1fr; }
        .ocr-thumbs { grid-column: 1 / 2; grid-row: 1 / 3; }
        .ocr-visual { grid-column: 2 / 3; grid-row: 1 / 2; border-right: none; border-bottom: 1px solid var(--border); }
        .ocr-edit { grid-column: 2 / 3; grid-row: 2 / 3; }
      }
    </style>
  `;

  /* ---------------- 缩略图列表 ---------------- */
  const thumbsList = mount.querySelector('#thumbs-list');
  for (let i = 1; i <= totalPages; i++) {
    const p = pages[String(i)] || {};
    const confirmed = confirmedPages.includes(i);
    const modified = p.modified;
    const div = document.createElement('div');
    div.className = `thumb-item${i === currentPage ? ' active' : ''}${confirmed ? ' confirmed' : ''}${modified ? ' modified' : ''}`;
    div.innerHTML = `<img loading="lazy" src="${thumbUrl(ocrDir, i)}" alt="p${i}"><div class="thumb-num">${i}${confirmed ? ' ✓' : ''}</div>`;
    div.addEventListener('click', () => goToPage(i));
    thumbsList.appendChild(div);
  }

  /* ---------------- 图片 + 文本 ---------------- */
  const imgEl = mount.querySelector('#page-img');
  const textEl = mount.querySelector('#page-text');
  const zoomPct = mount.querySelector('#zoom-pct');
  let zoom = 1;

  async function loadPage(pageNum) {
    currentPage = pageNum;

    // img
    imgEl.src = thumbUrl(ocrDir, pageNum);
    imgEl.style.transform = `scale(${zoom})`;
    zoomPct.textContent = `${Math.round(zoom * 100)}%`;

    // text
    let page = pages[String(pageNum)];
    if (!page) {
      const txt = await loadOcrText(ocrDir, pageNum);
      page = { text: txt, modified: false, confirmed: confirmedPages.includes(pageNum) };
      pages[String(pageNum)] = page;
    }
    textEl.value = page.text || '';
    mount.querySelector('#chk-confirmed').checked = !!page.confirmed;
    mount.querySelector('#page-num').value = pageNum;

    // 更新缩略图 active
    thumbsList.querySelectorAll('.thumb-item').forEach((t, i) => {
      t.classList.toggle('active', i + 1 === pageNum);
      t.classList.toggle('confirmed', confirmedPages.includes(i + 1));
      t.classList.toggle('modified', pages[String(i + 1)]?.modified);
    });

    // 翻页按钮状态
    mount.querySelector('#btn-prev').disabled = pageNum <= 1;
    mount.querySelector('#btn-next').disabled = pageNum >= totalPages;
  }

  function goToPage(n) {
    if (n < 1 || n > totalPages || n === currentPage) return;
    saveCurrentPage();  // 离开当前页前自动保存
    loadPage(n);
  }

  function saveCurrentPage() {
    const p = pages[String(currentPage)];
    if (!p) return;
    const newText = textEl.value;
    if (newText !== p.text) {
      p.text = newText;
      p.modified = true;
    }
  }

  async function persistAll() {
    // 确认 confirmedPages 与 pages 里的确认状态同步
    const newConfirmed = [];
    for (let i = 1; i <= totalPages; i++) {
      if (pages[String(i)]?.confirmed) newConfirmed.push(i);
    }
    await saveDraft(item, pages, currentPage, newConfirmed);
    toast(`已保存 · ${newConfirmed.length} 页待确认`, 'ok', 2000);
  }

  /* ---------------- 绑定事件 ---------------- */
  mount.querySelector('#btn-back').onclick = () => navigate('#/import');

  mount.querySelector('#btn-prev').onclick = () => goToPage(currentPage - 1);
  mount.querySelector('#btn-next').onclick = () => goToPage(currentPage + 1);
  mount.querySelector('#page-num').addEventListener('change', (e) => {
    const n = parseInt(e.target.value, 10);
    if (!isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
  });

  mount.querySelector('#zoom-in').onclick = () => { zoom = Math.min(3, zoom + 0.2); imgEl.style.transform = `scale(${zoom})`; zoomPct.textContent = `${Math.round(zoom * 100)}%`; };
  mount.querySelector('#zoom-out').onclick = () => { zoom = Math.max(0.3, zoom - 0.2); imgEl.style.transform = `scale(${zoom})`; zoomPct.textContent = `${Math.round(zoom * 100)}%`; };

  mount.querySelector('#btn-save-page').onclick = async () => {
    saveCurrentPage();
    await persistAll();
  };

  mount.querySelector('#chk-confirmed').onchange = async (e) => {
    saveCurrentPage();
    pages[String(currentPage)].confirmed = e.target.checked;
    await persistAll();
    loadPage(currentPage);  // 刷新缩略图状态
  };

  mount.querySelector('#btn-revert').onclick = async () => {
    if (!confirm('恢复本页为 OCR 原文？你在这页的修改会丢失。')) return;
    const txt = await loadOcrText(ocrDir, currentPage);
    pages[String(currentPage)] = { text: txt, modified: false, confirmed: pages[String(currentPage)]?.confirmed || false };
    textEl.value = txt;
    await persistAll();
  };

  mount.querySelector('#btn-commit').onclick = async () => {
    // 保存当前页
    saveCurrentPage();
    await persistAll();
    const n = confirm('确认把已勾选的页结构化后入题库？这些题目初始状态是「待确认」，你可以在题库里继续编辑。');
    if (!n) return;
    const extracted = await commitToBank(item, pages);
    toast(`✓ 共生成 ${extracted} 道题（待最终确认）`, 'ok', 4000);
    // 更新按钮禁用状态
    const newConfirmed = [];
    for (let i = 1; i <= totalPages; i++) {
      if (pages[String(i)]?.confirmed) newConfirmed.push(i);
    }
    mount.querySelector('#btn-commit').disabled = newConfirmed.length === 0;
  };

  // 文本编辑 → 自动标记 modified
  textEl.addEventListener('input', () => {
    pages[String(currentPage)] = pages[String(currentPage)] || { text: '', modified: false, confirmed: false };
    pages[String(currentPage)].text = textEl.value;
    pages[String(currentPage)].modified = true;
  });

  // 初始化
  await loadPage(currentPage);

  // 自动持久化：每 30 秒存一次，防止意外丢数据
  const autosave = setInterval(async () => {
    saveCurrentPage();
    await persistAll();
  }, 30000);
  // 离开页面时停止
  const stopAutoSave = () => { clearInterval(autosave); saveCurrentPage(); };
  window.addEventListener('hashchange', stopAutoSave, { once: true });
}
